import { IAbortController, IAbortControllerFactory, IImage } from '../../../infra';
import { IAutocompleteSuggestions } from '../model/AutocompleteSuggestions';
import { IConversation } from '../model/Conversation';
import { IPlacerAssistant } from '../model/PlacerAssistant';
import { IPlacerAssistantFactory } from '../model/PlacerAssistantFactory';
import { IPlacerAssistantService } from './PlacerAssistantService';
import { IConversationLoaderManager } from './ConversationLoaderManager';
import { IConversationsLoaderFactory } from './ConversationsLoaderFactory';
import {
    AdvancedEventEmitter,
    LoadingState,
    LoadingStateBroker,
} from '@zdrbm/zdr-native-tools';
import { IRunLifecycleManagerFactory } from './RunLifecycleManagerFactory';
import { RunStatus } from './AssistantDTOs';

export interface IPlacerAssistantFacade {
    getConversationLoadingState(conversationId: string): LoadingStateBroker;
    ensureConversation(conversationId: string): Promise<void>;
    getSuggestions(
        query: string,
        session?: string,
        source?: string,
    ): Promise<IAutocompleteSuggestions>;
    createConversation(userMessage: string, image: IImage | undefined): Promise<IConversation>;
    postUserMessage(
        conversationId: string,
        userMessage: string,
        image: IImage | undefined,
    ): Promise<void>;
    getModel(): IPlacerAssistant;
    getConversationsLoader(): IConversationLoaderManager;
    deleteConversation(conversationId: string): Promise<boolean>;
}

export type {
    IAutocompleteSuggestions,
    IConversation,
    IPlacerAssistant,
    IConversationLoaderManager,
};

export interface IPlacerAssistantFacadeServices {
    placerAssistantService: IPlacerAssistantService;
    abortControllerFactory: IAbortControllerFactory;
    placerAssistantFactory: IPlacerAssistantFactory;
    conversationsLoaderFactory: IConversationsLoaderFactory;
    runsLifecycleFactory: IRunLifecycleManagerFactory;
}

export class PlacerAssistantFacade extends AdvancedEventEmitter implements IPlacerAssistantFacade {
    private suggestionsSourceAbortControllers: Map<string, IAbortController> = new Map();
    private loadingStates: Map<string, LoadingStateBroker> = new Map();

    constructor(private model: IPlacerAssistant, private services: IPlacerAssistantFacadeServices) {
        super();
    }

    getConversationLoadingState(conversationId: string): LoadingStateBroker {
        let loadingState = this.loadingStates.get(conversationId);
        if (!loadingState) {
            const conversation = this.getModel().getConversation(conversationId);
            let initialState = conversation ? LoadingState.DONE : LoadingState.IDLE;
            loadingState = new LoadingStateBroker(this, initialState);
            this.loadingStates.set(conversationId, loadingState);
        }

        return loadingState;
    }

    async ensureConversation(conversationId: string): Promise<void> {
        const currentState = this.getConversationLoadingState(conversationId);
        if (currentState.get() === LoadingState.IDLE) {
            const result: IConversation | undefined =
                await this.services.placerAssistantService.getConversationById(conversationId);

            if (!result) {
                currentState.setError();
            } else {
                this.model.addConversation(result);
                currentState.setDone();
            }
        }
    }

    getConversationsLoader(): IConversationLoaderManager {
        return this.services.conversationsLoaderFactory.create();
    }

    deleteConversation(conversationId: string): Promise<boolean> {
        return this.services.placerAssistantService.deleteConversation(conversationId);
    }

    getModel(): IPlacerAssistant {
        return this.model;
    }

    async postUserMessage(
        conversationId: string,
        userMessage: string,
        image: IImage | undefined,
    ): Promise<void> {
        const conversation = this.model.getConversation(conversationId);
        if (!conversation) {
            throw new Error(`Conversation with id ${conversationId} not found`);
        }
        const newRun = this.services.placerAssistantFactory.createConversationRun({
            id: undefined,
            conversation_id: conversationId,
            input: userMessage,
            containers: [],
            status: RunStatus.PENDING,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            started_at: new Date().toISOString(),
            resources: [],
        });
        conversation.addRun(newRun);

        const run = await this.services.placerAssistantService.sendMessage(
            conversationId,
            userMessage,
            image,
        );
        newRun.setId(run.getId());
        newRun.updateFrom(run);

        const runLifecycleManager = this.services.runsLifecycleFactory.create(newRun);

        runLifecycleManager.start();

    }

    private async generateConversationTitle(conversationId: string) {
        const conversation = this.model.getConversation(conversationId);
        if (conversation) {
            const result =
                await this.services.placerAssistantService.generateConversationTitle(
                    conversationId,
                );
            conversation.setTitle(result.title.get());
        }
    }

    async createConversation(
        userMessage: string,
        image: IImage | undefined,
    ): Promise<IConversation> {
        const conversation = await this.services.placerAssistantService.createConversation();

        this.model.addConversation(conversation);

        await this.postUserMessage(conversation.getId(), userMessage, image);

        // Not awaiting - this can be done in the background
        this.generateConversationTitle(conversation.getId());

        return conversation;
    }

    async getSuggestions(
        query: string,
        session?: string,
        source?: string,
    ): Promise<IAutocompleteSuggestions> {
        let abortController: IAbortController | undefined;

        if (source) {
            abortController = this.suggestionsSourceAbortControllers.get(source);
            if (abortController) {
                abortController.abort();
            } else {
                abortController = this.services.abortControllerFactory.createAbortController();
                this.suggestionsSourceAbortControllers.set(source, abortController);
            }
        }

        const suggestions = await this.services.placerAssistantService.getSuggestions(
            query,
            session,
            abortController,
        );

        return suggestions;
    }
}
